import React, {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import find from 'lodash/find';
import reverse from 'lodash/reverse';
import _sortBy from 'lodash/sortBy';
import reduce from 'lodash/reduce';

import {tableStyles} from './styles';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import {AutoSizer, Column, SortDirection, Table} from 'react-virtualized';

function SimpleTable({
  list,
  columns,
  rowClassName,
  rowHeight,
  headerHeight,
  onRowClick,
  sortBy,
  sortDirection,
  sortKey,
  ...tableProps
}) {

  const classes = tableStyles();

  const [state, setState] = useState({sortBy, sortDirection, sortedList: list});

  const _sort = useCallback(({sortBy, sortDirection}) => {
    const sortKey = get(find(columns, {dataKey: sortBy}), 'sortKey', sortBy);
    if (sortDirection === SortDirection.DESC) {
      const sortedList = reverse(_sortBy(list, sortKey))
      setState({sortBy, sortDirection, sortedList});
    } else {
      const sortedList = _sortBy(list, sortKey);
      setState({sortBy, sortDirection, sortedList})
    }
  }, [list, columns]);

  useEffect(() => {
    setState(prevState => ({...prevState, sortedList: list}));
    _sort({sortBy, sortDirection})
  }, [list, setState, sortBy, sortDirection, _sort]);

  const getRowClassName = ({index}) => {
    return classNames(classes.tableRow, classes.flexContainer, rowClassName, {
      [classes.tableRowHover]: index !== -1 && onRowClick != null,
    })};

  const rowGetter = ({index}) => get(state, ['sortedList', index], '');

  const cellRenderer = ({cellData}) =>
    <TableCell
      component="div"
      className={classNames(classes.tableCell, classes.flexContainer, {[classes.noClick]: onRowClick == null})}
      variant="body"
      style={{height: rowHeight}}
    >
      {cellData}
    </TableCell>;

  const headerRenderer = ({label, dataKey, sortBy, sortDirection, disableSort}) => {
    const direction = {
      [SortDirection.ASC]: 'asc',
      [SortDirection.DESC]: 'desc',
    };
    return (
      <TableCell
        component='div'
        className={classNames(classes.tableCell, classes.flexContainer, classes.noClick)}
        variant='head'
        style={{height: headerHeight}}
      >
        {!disableSort
          ? <TableSortLabel active={dataKey === sortBy} direction={direction[sortDirection]}>
            {label}
          </TableSortLabel>
          : label
        }
      </TableCell>
    );
  };

  const _noRowsRenderer = () => {
    return <div className={classes.noRows}><p>Nenhum Item Encontrado.</p></div>;
  };

  const tableWidth = reduce(columns, (acc, el) => el.width + acc, 0);

  return <AutoSizer>
    {({height, width}) => (
      <Table
        className={classes.table}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        height={height - 20}
        width={Math.max(tableWidth, width)}
        rowGetter={rowGetter}
        noRowsRenderer={_noRowsRenderer}
        rowClassName={getRowClassName}
        sort={_sort}
        sortBy={state.sortBy}
        sortDirection={state.sortDirection}
        onRowClick={onRowClick}
        {...tableProps}
      >
        {columns.map(({ cellContentRenderer = null, className, dataKey, ...other }, index) => {
          let renderer;
          if (cellContentRenderer !== null) {
            renderer = cellRendererProps =>
              cellRenderer({ cellData: cellContentRenderer(cellRendererProps), columnIndex: index });
          } else {
            renderer = cellRenderer;
          }
          return (
            <Column
              key={dataKey}
              headerRenderer={headerRenderer}
              className={classNames(classes.flexContainer, className)}
              cellRenderer={renderer}
              dataKey={dataKey}
              {...other}
            />
          );
        })}
      </Table>
    )}
  </AutoSizer>;
}

SimpleTable.propTypes = {
  classes: PropTypes.object.isRequired,
  list: PropTypes.object,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      cellContentRenderer: PropTypes.func,
      dataKey: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
    }),
  ).isRequired,
  headerHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  rowClassName: PropTypes.string,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  sort: PropTypes.func,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  overscanRowCount: PropTypes.number,
  tableWidth: PropTypes.number
};

SimpleTable.defaultProps = {
  headerHeight: 56,
  rowHeight: 56,
  overscanRowCount: 10,
  sortBy: 'validFrom',
  sortDirection: SortDirection.DESC
};
    export default SimpleTable;
